import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConflictError, apiFetch, apiJson, apiSend, apiUpload } from "@/api/client";

/* Mirrors api/src/pbo_invoicing/drive/schemas.py. No Google token ever reaches the browser. */

export type DriveStatus = {
  /** False when the server has no Google client configured (PBO_GOOGLE_CLIENT_ID). */
  set_up: boolean;
  status: "not_connected" | "connected" | "needs_reconnect";
  account_email: string;
  connected_at: string | null;
  connected_by: string;
  folder_url: string | null;
};

export type UploadKind = "bill" | "proof";
export type DriveFile = { url: string; name: string; file_id: string };

/** The server's limit (domain/uploads.py); checked here so nginx's HTML 413 is never seen. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const UPLOAD_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";

export const driveKeys = { status: ["drive"] as const };

export function getDriveStatus(): Promise<DriveStatus> {
  return apiJson<DriveStatus>("/drive");
}

export function connectDrive(): Promise<{ auth_url: string }> {
  return apiSend<{ auth_url: string }>("/drive/connect", "POST");
}

export function disconnectDrive(): Promise<DriveStatus> {
  return apiSend<DriveStatus>("/drive/disconnect", "POST");
}

/**
 * File a bill or a payment proof in the company Drive. `on` (YYYY-MM-DD) picks the year and
 * month folder; `label` (the payee, or what it was for) goes into the file's name.
 */
export async function uploadToDrive(
  file: File,
  kind: UploadKind,
  on: string,
  label = "",
): Promise<DriveFile> {
  if (file.size > MAX_UPLOAD_BYTES) throw new Error("The file must be at most 10 MB");
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  form.append("on", on);
  form.append("label", label);
  return apiUpload<DriveFile>("/drive/files", "POST", form);
}

// A Drive file link, as the app makes it or as Drive's "Copy link" gives it (google.py).
const FILE_LINK = /^https:\/\/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]{10,64})(?:[/?#].*)?$/;

/** The file id in a Drive file link; null for any other link. */
export function driveFileId(url: string): string | null {
  return FILE_LINK.exec(url)?.[1] ?? null;
}

/** A bill or proof an expense links to, read back through the API to show in the viewer. */
export async function fetchDriveFile(fileId: string): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`/drive/files/${encodeURIComponent(fileId)}/content`);
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return { blob: await res.blob(), filename: match?.[1] ?? "document" };
}

export function useDriveStatus() {
  return useQuery({ queryKey: driveKeys.status, queryFn: getDriveStatus });
}

export function useDisconnectDrive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectDrive,
    onSuccess: (status) => queryClient.setQueryData(driveKeys.status, status),
  });
}

/** A 409 means Drive is not connected or needs reconnecting, so the status is read again. */
export function useUploadToDrive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { file: File; kind: UploadKind; on: string; label: string }) =>
      uploadToDrive(args.file, args.kind, args.on, args.label),
    onError: (error) => {
      if (error instanceof ConflictError) {
        void queryClient.invalidateQueries({ queryKey: driveKeys.status });
      }
    },
  });
}
